import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import type { ConversationItem, DirectMessage, FriendItem } from '@xiangqi/protocol';
import { api, currentUser, ensureSession } from '../api.ts';
import { Avatar } from './FriendsScreen.tsx';

interface Props { navigate(path: string): void }

export function ChatScreen({ navigate }: Props) {
  const [conversations, setConversations] = useState<ConversationItem[]>([]);
  const [friends, setFriends] = useState<FriendItem[]>([]);
  const [peerId, setPeerId] = useState(() => new URLSearchParams(location.search).get('with'));
  const [messages, setMessages] = useState<DirectMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const messagesEnd = useRef<HTMLDivElement | null>(null);
  const selfId = currentUser()?.user_id;
  const peer = useMemo(() => [...friends, ...conversations].find((item) => item.user_id === peerId), [friends, conversations, peerId]);

  const refreshConversations = async () => {
    const [chatResult, friendResult] = await Promise.all([api.conversations(), api.friends()]);
    setConversations(chatResult.conversations);
    setFriends(friendResult.friends);
  };
  useEffect(() => {
    let cancelled = false;
    void ensureSession().then(refreshConversations).catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Không tải được tin nhắn.'); });
    const timer = window.setInterval(() => { void refreshConversations().catch(() => undefined); }, 8000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, []);
  useEffect(() => {
    const syncPeerFromUrl = () => setPeerId(new URLSearchParams(location.search).get('with'));
    window.addEventListener('popstate', syncPeerFromUrl);
    return () => window.removeEventListener('popstate', syncPeerFromUrl);
  }, []);
  useEffect(() => {
    if (!peerId) { setMessages([]); return undefined; }
    let cancelled = false;
    const load = () => void api.messages(peerId).then((res) => { if (!cancelled) { setMessages(res.messages); setError(null); } }).catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : 'Không tải được hội thoại.'); });
    load();
    const timer = window.setInterval(load, 3000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, [peerId]);
  useEffect(() => { messagesEnd.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [messages]);

  const open = (id: string) => { setPeerId(id); setError(null); navigate(`/chat?with=${id}`); };
  const send = async (event: FormEvent) => {
    event.preventDefault();
    const body = draft.trim();
    if (!body || !peerId || sending) return;
    setSending(true); setError(null);
    try {
      const result = await api.sendMessage(peerId, body);
      setMessages((old) => old.some((message) => message.message_id === result.message.message_id) ? old : [...old, result.message]); setDraft(''); await refreshConversations();
    } catch (e) { setError(e instanceof Error ? e.message : 'Gửi tin nhắn thất bại.'); }
    finally { setSending(false); }
  };

  return <main className="page social-page chat-page">
    <header className="social-page-header"><button className="back-button" onClick={() => peerId ? open('') : navigate('/')}>‹</button><div><h1>{peer?.display_name ?? 'Trò chuyện'}</h1><p>{peerId ? 'Tin nhắn riêng với bạn bè' : 'Kết nối và trò chuyện cùng bạn bè'}</p></div></header>
    {error && <p className="social-alert" role="status">{error}</p>}
    {!peerId ? <section className="chat-contacts">
      {conversations.length > 0 && <><h2>Gần đây</h2>{conversations.map((item) => <button className="social-player chat-contact" key={item.user_id} onClick={() => open(item.user_id)}><Avatar name={item.display_name}/><span className="social-player-info"><strong>{item.display_name}</strong><small>{item.last_message}</small></span>{item.unread_count > 0 && <span className="chat-unread">{item.unread_count}</span>}</button>)}</>}
      <h2>Bạn bè</h2>{friends.length ? friends.map((friend) => <button className="social-player chat-contact" key={friend.user_id} onClick={() => open(friend.user_id)}><Avatar name={friend.display_name}/><span className="social-player-info"><strong>{friend.display_name}</strong><small>Bắt đầu trò chuyện</small></span><span>›</span></button>) : <div className="social-empty"><span>•••</span><strong>Chưa có hội thoại</strong><small>Kết bạn để bắt đầu trò chuyện.</small><button className="social-primary" onClick={() => navigate('/friends')}>Tìm bạn bè</button></div>}
    </section> : <>
      <section className="chat-message-list" aria-label="Tin nhắn">{messages.length === 0 ? <div className="chat-start-hint">Bắt đầu cuộc trò chuyện với {peer?.display_name ?? 'bạn bè'}.</div> : messages.map((message) => { const mine = message.sender_id === selfId; return <div className={`chat-bubble-row ${mine ? 'mine' : ''}`} key={message.message_id}><div className="chat-bubble">{message.body}<small>{new Date(message.created_at).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })}</small></div></div>; })}<div ref={messagesEnd} /></section>
      <form className="chat-compose" onSubmit={(event) => void send(event)}><input value={draft} onChange={(e) => setDraft(e.target.value)} maxLength={1000} placeholder="Nhập tin nhắn…" aria-label="Tin nhắn" /><button className="social-primary" disabled={!draft.trim() || sending}>{sending ? '…' : 'Gửi'}</button></form>
    </>}
  </main>;
}
